import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, finalize, map } from 'rxjs/operators';
import { DiversionRoutes, Order, PO } from '~lmo/models/order.model';
import { LmoOrdersService } from '~lmo/services';
import { trackById } from '~utilities/trackById';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-orders-divert-dialog',
  styleUrls: ['./lmo-orders-divert-dialog.component.scss'],
  templateUrl: './lmo-orders-divert-dialog.component.html',
})
export class LmoOrdersDivertDialogComponent implements OnInit {
  public diversionRoutes$: Observable<DiversionRoutes[]>;
  public purchaseOrders$$ = new BehaviorSubject<PO[]>([]);
  public networkActive$ = new BehaviorSubject(false);
  public diversionOrderForm: FormGroup;
  public trackById = trackById;

  constructor(
    private orderService: LmoOrdersService,
    public dialogRef: MatDialogRef<LmoOrdersDivertDialogComponent>,
    private fb: FormBuilder,
    public snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: Order = data,
  ) {}

  public ngOnInit() {
    this.diversionOrderForm = this.fb.group({
      POId: this.fb.control({ value: null, disabled: true }, [Validators.required]),
      fracId: ['', [Validators.required]],
    });

    this.diversionRoutes$ = this.orderService.orderDiversionRoutes$(this.data.id).pipe(
      map((wells) => {
        if (!wells || wells.length === 0) {
          this.snackBar.open(`No wells are available for diversion`, null, {
            duration: 2500,
          });
          this.dialogRef.close();
        }
        wells = wells && wells.filter((well) => well.fracId !== this.data.fracId);
        return wells.sort((a, b) => {
          if (a.totalStorageAmount - a.totalVolumeInStorage > 0 && b.totalStorageAmount - b.totalVolumeInStorage > 0) {
            return a.timeToReachWell > 0 ? a.timeToReachWell - b.timeToReachWell : b.timeToReachWell;
          } else {
            return (
              -1 * (a.totalStorageAmount - a.totalVolumeInStorage - (b.totalStorageAmount - b.totalVolumeInStorage))
            );
          }
        });
      }),
    );
    this.setupFormDisableAndEnableListeners();
  }

  private setupFormDisableAndEnableListeners() {
    this.diversionOrderForm
      .get('fracId')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe((fracValue) => {
        if (!fracValue) {
          this.diversionOrderForm.get('POId').disable();
        } else {
          this.diversionOrderForm.get('POId').enable();
          this.diversionRoutes$.subscribe((orders) => {
            this.purchaseOrders$$.next(orders.find((order) => order.fracId === fracValue.fracId).purchaseOrders);
          });
        }
      });
  }

  public divertFrac() {
    this.diversionOrderForm.markAllAsTouched();
    if (this.diversionOrderForm.invalid) {
      return;
    }
    const formValue = {
      fracId: this.diversionOrderForm.get('fracId').value.fracId,
      purchaseOrderId: this.diversionOrderForm.get('POId').value,
    };
    this.divertOrder(formValue);
  }

  public trackByFracId(_index: number, frac: DiversionRoutes) {
    return frac.fracId;
  }

  public trackByPOId(_index: number, po: PO) {
    return po.id;
  }

  public divertOrder(formValue) {
    this.networkActive$.next(true);
    this.orderService
      .divertOrder(this.data.id, formValue)
      .pipe(
        finalize(() => {
          this.networkActive$.next(false);
        }),
      )
      .subscribe(
        (response) => {
          this.snackBar.open(`Order is diverted successfully`, null, {
            duration: 2500,
          });
          this.dialogRef.close();
        },
        (err) => {
          this.snackBar.open('Something Went Wrong', null, {
            duration: 2500,
            panelClass: ['snackbar-error'],
          });
        },
      );
  }
}
